import { motion } from "framer-motion";
import {
  Send,
  Eye,
  MousePointer,
  TrendingUp,
  Mail,
  Award,
  BarChart3,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import type { EmailAnalytics as EmailAnalyticsType } from "@/types/index";

interface EmailAnalyticsProps {
  analytics: EmailAnalyticsType | undefined;
  isLoading: boolean;
}

// Colors for preference pie chart
const PREFERENCE_COLORS = {
  morning: "#f59e0b",
  afternoon: "#f97316",
  evening: "#6366f1",
  nightly: "#8b5cf6",
  weekly: "#3b82f6",
};

// Generate mock performance data based on analytics
// In a real implementation, this would come from the analytics API
function generatePerformanceData(analytics: EmailAnalyticsType | undefined) {
  if (!analytics) return [];

  // Generate last 7 data points with realistic variations
  const baseOpenRate = analytics.openRate;
  const baseClickRate = analytics.clickRate;

  return [
    { date: "Week 1", opens: Math.round(baseOpenRate * 0.85), clicks: Math.round(baseClickRate * 0.82) },
    { date: "Week 2", opens: Math.round(baseOpenRate * 0.92), clicks: Math.round(baseClickRate * 0.88) },
    { date: "Week 3", opens: Math.round(baseOpenRate * 0.88), clicks: Math.round(baseClickRate * 0.85) },
    { date: "Week 4", opens: Math.round(baseOpenRate * 0.95), clicks: Math.round(baseClickRate * 0.92) },
    { date: "Week 5", opens: Math.round(baseOpenRate * 0.98), clicks: Math.round(baseClickRate * 0.95) },
    { date: "Week 6", opens: Math.round(baseOpenRate * 0.93), clicks: Math.round(baseClickRate * 0.90) },
    { date: "This Week", opens: Math.round(baseOpenRate), clicks: Math.round(baseClickRate) },
  ];
}

// Generate mock best performing emails
// In a real implementation, this would come from a separate API endpoint
function generateTopEmails(analytics: EmailAnalyticsType | undefined) {
  if (!analytics) return [];

  const baseOpenRate = analytics.openRate;
  const baseClickRate = analytics.clickRate;

  return [
    { subject: "Breaking: Major Market Shift", openRate: Math.round(baseOpenRate * 1.15), clickRate: Math.round(baseClickRate * 1.2), sent: "Today" },
    { subject: "Weekly Digest: Top Stories", openRate: Math.round(baseOpenRate * 1.08), clickRate: Math.round(baseClickRate * 1.1), sent: "Yesterday" },
    { subject: "Exclusive: Industry Analysis", openRate: Math.round(baseOpenRate * 1.02), clickRate: Math.round(baseClickRate * 1.05), sent: "2 days ago" },
    { subject: "Deep Dive: Economic Outlook", openRate: Math.round(baseOpenRate * 0.98), clickRate: Math.round(baseClickRate * 1.02), sent: "3 days ago" },
    { subject: "Morning Brief: Key Insights", openRate: Math.round(baseOpenRate * 0.95), clickRate: Math.round(baseClickRate * 0.98), sent: "4 days ago" },
  ];
}

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  iconColor: string;
  trend?: number;
  delay?: number;
  isLoading?: boolean;
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  iconColor,
  trend,
  delay = 0,
  isLoading,
}: MetricCardProps) {
  if (isLoading) {
    return (
      <Card className="p-5">
        <div className="flex items-start justify-between">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <Skeleton className="w-16 h-5 rounded-full" />
        </div>
        <Skeleton className="h-8 w-24 mt-4" />
        <Skeleton className="h-4 w-32 mt-2" />
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay * 0.1, duration: 0.4 }}
    >
      <Card className="p-5 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className={cn("p-2.5 rounded-xl", iconColor)}>{icon}</div>
          {trend !== undefined && (
            <div
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                trend >= 0
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                  : "bg-red-500/10 text-red-600 dark:text-red-400"
              )}
            >
              <TrendingUp
                className={cn("w-3 h-3", trend < 0 && "rotate-180")}
              />
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-3xl font-semibold tabular-nums">{value}</p>
          <p className="text-sm text-muted-foreground mt-1">{title}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground/70 mt-0.5">{subtitle}</p>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

// Custom tooltip for line chart
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium mb-2">{label}</p>
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center gap-2 text-sm">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-muted-foreground capitalize">{entry.name}:</span>
          <span className="font-medium">{entry.value}%</span>
        </div>
      ))}
    </div>
  );
}

// Custom legend for pie chart
function CustomLegend({ payload }: any) {
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-4">
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm text-muted-foreground capitalize">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function EmailAnalytics({ analytics, isLoading }: EmailAnalyticsProps) {
  // Calculate preference data for pie chart
  const preferenceData = analytics
    ? Object.entries(analytics.preferenceBreakdown)
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({
          name,
          value,
          color: PREFERENCE_COLORS[name as keyof typeof PREFERENCE_COLORS],
        }))
    : [];

  // Generate performance data from analytics
  const performanceData = generatePerformanceData(analytics);

  // Generate top emails from analytics
  const topEmails = generateTopEmails(analytics);

  // Calculate trends (mock data - in real app would compare with previous period)
  const emailsSentTrend = 12.5;
  const openRateTrend = 8.2;
  const clickRateTrend = 15.3;
  const subscriberTrend = 5.7;

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Emails Sent"
          value={analytics?.emailsSentWeek.toLocaleString() ?? 0}
          subtitle="This week"
          icon={<Send className="w-5 h-5" />}
          iconColor="bg-blue-500/10 text-blue-500"
          trend={emailsSentTrend}
          delay={0}
          isLoading={isLoading}
        />
        <MetricCard
          title="Open Rate"
          value={`${analytics?.openRate.toFixed(1) ?? 0}%`}
          subtitle="Industry avg: 45%"
          icon={<Eye className="w-5 h-5" />}
          iconColor="bg-emerald-500/10 text-emerald-500"
          trend={openRateTrend}
          delay={1}
          isLoading={isLoading}
        />
        <MetricCard
          title="Click Rate"
          value={`${analytics?.clickRate.toFixed(1) ?? 0}%`}
          subtitle="Industry avg: 12%"
          icon={<MousePointer className="w-5 h-5" />}
          iconColor="bg-violet-500/10 text-violet-500"
          trend={clickRateTrend}
          delay={2}
          isLoading={isLoading}
        />
        <MetricCard
          title="Active Subscribers"
          value={analytics?.activeSubscribers.toLocaleString() ?? 0}
          subtitle={`${analytics?.unsubscribed ?? 0} unsubscribed`}
          icon={<Mail className="w-5 h-5" />}
          iconColor="bg-amber-500/10 text-amber-500"
          trend={subscriberTrend}
          delay={3}
          isLoading={isLoading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <motion.div
          className="lg:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-muted-foreground" />
                  Email Performance
                </CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-muted-foreground">Opens</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <span className="text-muted-foreground">Clicks</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="w-full h-[300px]" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={performanceData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="opens"
                      name="opens"
                      stroke="hsl(var(--chart-2))"
                      strokeWidth={2.5}
                      dot={{ fill: "hsl(var(--chart-2))", strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="clicks"
                      name="clicks"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2.5}
                      dot={{ fill: "hsl(var(--primary))", strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Preference Breakdown Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">
                Preference Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-[300px]">
                  <Skeleton className="w-48 h-48 rounded-full" />
                  <div className="flex gap-4 mt-6">
                    <Skeleton className="w-16 h-4" />
                    <Skeleton className="w-16 h-4" />
                    <Skeleton className="w-16 h-4" />
                  </div>
                </div>
              ) : preferenceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={preferenceData}
                      cx="50%"
                      cy="45%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {preferenceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => [
                        `${value.toLocaleString()} subscribers`,
                        "",
                      ]}
                    />
                    <Legend content={<CustomLegend />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                  <Mail className="w-12 h-12 opacity-30 mb-2" />
                  <p className="text-sm">No preference data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Best Performing Emails */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              Best Performing Emails
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-64" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <div className="flex gap-8">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : topEmails.length > 0 ? (
              <div className="divide-y divide-border">
                {topEmails.map((email, index) => (
                  <motion.div
                    key={email.subject}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.05 }}
                    className="flex items-center justify-between py-3.5 first:pt-0 last:pb-0 hover:bg-muted/30 -mx-4 px-4 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate pr-4">{email.subject}</p>
                      <p className="text-sm text-muted-foreground">
                        Sent {email.sent}
                      </p>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <p className="font-medium text-emerald-600 dark:text-emerald-400">
                          {email.openRate}%
                        </p>
                        <p className="text-xs text-muted-foreground">Open</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-primary">
                          {email.clickRate}%
                        </p>
                        <p className="text-xs text-muted-foreground">Click</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Send className="w-12 h-12 opacity-30 mb-2" />
                <p className="text-sm">No email performance data yet</p>
                <p className="text-xs">Send some campaigns to see stats</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
